"use client";

import { useEffect, useState } from "react";
import type { Heading } from "@/lib/blogArticle";

/** The design's "On This Page" rail: a sticky card listing the article's own
 *  h2/h3 headings, with the one currently in view picked out.
 *
 *  Measured off Frame 255 of 1306:1357 — 285 wide on radius 10, #f2f2f2 over
 *  a #d1d1d1 hairline (brand-veil on brand-line-ink), with Frame 9 at y=30
 *  and Frame 14 at x=20 giving the 30px top and bottom and 20px sides.
 *  Heading in 20px Outfit Medium #447973, rows starting 10px under it at
 *  29px each, flush with the heading rather than indented under it.
 *
 *  The current row is a chip: a pale ground with an accent bar down its LEFT
 *  edge and the corners rounded off the other end. Not a bordered box — a
 *  full outline inside the card the design already draws is the thing that
 *  looked wrong, and it is the left bar, not an outline, that marks the row.
 *
 *  Group 14 (245x29) reads with no fill or stroke because it is an inactive
 *  row; the active chip is among the records the .fig reader loses to a bad
 *  node, so its ground and bar are taken from the design image and built
 *  from the page's own tokens.
 *
 *  Highlighting uses an IntersectionObserver over a band across the upper
 *  third of the viewport rather than scroll offsets, so it stays correct
 *  through images loading and the header's own height changes.
 */
export function ArticleToc({ headings }: { headings: Heading[] }) {
  const [active, setActive] = useState<string | null>(headings[0]?.id ?? null);

  useEffect(() => {
    if (headings.length === 0) return;
    const els = headings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;

    const seen = new Map<string, boolean>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) seen.set(e.target.id, e.isIntersecting);
        // The topmost heading inside the band wins; document order, not
        // observer callback order, decides.
        const current = headings.find((h) => seen.get(h.id));
        if (current) setActive(current.id);
      },
      // Band from 96px below the top to 65% up from the bottom.
      { rootMargin: "-96px 0px -65% 0px", threshold: 0 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav
      aria-label="On this page"
      className="rounded-[10px] border border-brand-line-ink bg-brand-veil px-5 py-[30px] lg:sticky lg:top-28"
    >
      <p className="text-[1.25rem] font-medium leading-none text-brand-accent">On This Page</p>
      {/* -ml-2.5 cancels the rows' own left inset so their text still lines
          up with the heading, while the chip keeps room around its label. */}
      <ul className="-ml-2.5 mt-2.5 space-y-0.5">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              aria-current={active === h.id ? "true" : undefined}
              className={[
                // The transparent bar on every row keeps the labels on one
                // left edge, so the current row gains its marker without the
                // list shifting sideways as it moves.
                "flex items-start gap-1.5 rounded-r-[8px] border-l-[3px] py-[7px] pr-2 text-[0.9375rem] font-light leading-snug transition-colors",
                h.level === 3 ? "pl-5" : "pl-2.5",
                active === h.id
                  ? "border-brand-accent bg-brand-paper text-brand-ink"
                  : "border-transparent text-black hover:text-brand-accent-dark",
              ].join(" ")}
            >
              {/* An infographic or a video is a different kind of destination
                  from a section, so it is marked as one. The indent alone
                  reads as "subsection" and would misdescribe it. */}
              {h.kind && h.kind !== "heading" && (
                <svg
                  aria-hidden
                  viewBox="0 0 16 16"
                  className="mt-[3px] h-3.5 w-3.5 shrink-0 text-brand-accent"
                  fill="currentColor"
                >
                  {h.kind === "video" ? (
                    <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM6.5 5.2l4.2 2.8-4.2 2.8V5.2z" />
                  ) : (
                    <path d="M2 3.5h12v9H2v-9zm1.2 7.8h9.6L10 7.6l-2.1 2.6-1.4-1.6-3.3 2.7z" />
                  )}
                </svg>
              )}
              <span>{h.text}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
